import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Users, Trophy, Sparkles, Clock, CheckCircle2, DollarSign, Calendar as CalIcon, Mail, ClipboardList, Trash2, Reply, ChevronDown, ChevronUp, X, Upload, GripVertical, ArrowUp, ArrowDown, UsersRound, Shield } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUS_ORDER = ["submitted","under_review","approved","waitlisted","paid"];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [preStats, setPreStats] = useState(null);
  const [preList, setPreList] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [ann, setAnn] = useState({ title: "", body: "", category: "General", pinned: false });
  const [event, setEvent] = useState({ title: "", date: "", time: "", location: "", sport: "football", division: "", event_type: "practice", opponent: "", notes: "", directions_url: "" });
  const [sponsor, setSponsor] = useState({ name: "", level: "Community Sponsor", website: "", logo_url: "", story: "", quote: "" });
  const [sponsors, setSponsors] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [replyModal, setReplyModal] = useState(null); // { kind, inquiry }
  const [replySubj, setReplySubj] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [replyFiles, setReplyFiles] = useState([]); // [{file, name, size, mime, progress}]
  const [replyDragOver, setReplyDragOver] = useState(false);
  const REPLY_ALLOWED = /^(image\/(png|jpe?g|gif|webp|heic|heif)|application\/pdf|application\/msword|application\/vnd\.openxmlformats-officedocument\.wordprocessingml\.document)$/;
  const REPLY_MAX_FILE = 10 * 1024 * 1024;
  const REPLY_MAX_TOTAL = 25 * 1024 * 1024;
  const [expandedRow, setExpandedRow] = useState(null);
  const [regSort, setRegSort] = useState("created_desc");
  const [regFilterSport, setRegFilterSport] = useState("all");
  const [regFilterStatus, setRegFilterStatus] = useState("all");
  const [preSort, setPreSort] = useState("created_desc");
  const [prePgm, setPrePgm] = useState("all");
  const [spSort, setSpSort] = useState("manual");
  const [contactSort, setContactSort] = useState("created_desc");
  const [inquirySort, setInquirySort] = useState("created_desc");
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [teams, setTeams] = useState([]);
  const [teamForm, setTeamForm] = useState({ name: "", sport: "football", division: "", coach_name: "", notes: "" });

  const load = async () => {
    const [s, a, c, i, ps, pl, sp, tm] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/athletes"),
      api.get("/admin/contact"), api.get("/admin/sponsor-inquiries"),
      api.get("/admin/pre-registrations/stats"), api.get("/admin/pre-registrations"),
      api.get("/sponsors"),
      api.get("/admin/teams").catch(() => ({ data: [] })),
    ]);
    setStats(s.data); setAthletes(a.data); setContacts(c.data); setInquiries(i.data);
    setPreStats(ps.data); setPreList(pl.data); setSponsors(sp.data);
    setTeams(tm.data);
  };

  // Poll admin stats + inbox every 15s so unread badges update without manual refresh
  React.useEffect(() => {
    const t = setInterval(async () => {
      try {
        const [s, c, i] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/contact"),
          api.get("/admin/sponsor-inquiries"),
        ]);
        setStats(s.data); setContacts(c.data); setInquiries(i.data);
      } catch { /* silent — next tick retries */ }
    }, 15000);
    return () => clearInterval(t);
  }, []);

  const markSponsorRead = async (id) => {
    try { await api.patch(`/admin/sponsor-inquiries/${id}/read`); } catch { /* non-fatal */ }
  };
  const markContactRead = async (id) => {
    try { await api.patch(`/admin/contact/${id}/read`); } catch { /* non-fatal */ }
  };

  const deleteAthlete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this registration? This action cannot be undone.")) return;
    try { await api.delete(`/admin/athletes/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deletePre = async (id) => {
    if (!window.confirm("Are you sure you want to delete this pre-registration? This action cannot be undone.")) return;
    try { await api.delete(`/admin/pre-registrations/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openReply = (kind, inquiry) => {
    setReplyModal({ kind, inquiry });
    setReplySubj(kind === "sponsor_inquiry" ? `Re: SCU Sponsorship — ${inquiry.business_name || ""}` : `Re: ${inquiry.subject || "Your message to SCU"}`);
    setReplyBody("");
    setReplyFiles([]);
  };
  const addReplyFiles = (incoming) => {
    const next = [...replyFiles];
    for (const file of incoming) {
      if (!REPLY_ALLOWED.test(file.type)) { toast.error(`Unsupported file type: ${file.name}. Allowed: images, PDF, Word.`); continue; }
      if (file.size > REPLY_MAX_FILE) { toast.error(`${file.name} is over the 10 MB per-file limit.`); continue; }
      const projectedTotal = next.reduce((s, f) => s + f.size, 0) + file.size;
      if (projectedTotal > REPLY_MAX_TOTAL) { toast.error("Total attachments can't exceed 25 MB."); break; }
      if (next.some((f) => f.name === file.name && f.size === file.size)) continue;
      next.push({ file, name: file.name, size: file.size, mime: file.type, progress: 0 });
    }
    setReplyFiles(next);
  };
  const removeReplyFile = (idx) => setReplyFiles((prev) => prev.filter((_, i) => i !== idx));
  const sendReply = async () => {
    if (!replyModal || !replyBody.trim()) return;
    setReplySending(true);
    const fd = new FormData();
    fd.append("kind", replyModal.kind);
    fd.append("inquiry_id", replyModal.inquiry.id);
    fd.append("subject", replySubj);
    fd.append("message", replyBody);
    replyFiles.forEach((f) => fd.append("files", f.file, f.name));
    try {
      await api.post("/admin/reply", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => {
          if (!ev.total) return;
          const pct = Math.min(100, Math.round((ev.loaded / ev.total) * 100));
          setReplyFiles((prev) => prev.map((f) => ({ ...f, progress: pct })));
        },
      });
      toast.success(replyFiles.length ? `Reply sent with ${replyFiles.length} attachment${replyFiles.length === 1 ? "" : "s"}` : "Reply sent");
      setReplyModal(null); setReplyBody(""); setReplyFiles([]); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setReplySending(false); }
  };

  const handleLogoDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) readLogoFile(file);
  };
  const readLogoFile = (file) => {
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2MB"); return; }
    const r = new FileReader();
    r.onload = () => setSponsor((s) => ({ ...s, logo_url: r.result }));
    r.readAsDataURL(file);
  };

  const filteredAthletes = React.useMemo(() => {
    let arr = [...athletes];
    if (regFilterSport !== "all") arr = arr.filter(a => (a.sport || a.program) === regFilterSport);
    if (regFilterStatus !== "all") arr = arr.filter(a => a.status === regFilterStatus);
    const [key, dir] = regSort.split("_");
    const sign = dir === "asc" ? 1 : -1;
    const getVal = (o) => {
      if (key === "name") return `${o.first_name || ""} ${o.last_name || ""}`.trim().toLowerCase();
      if (key === "age") return Number(o.age_as_of_cutoff ?? 0);
      if (key === "sport") return (o.sport || o.program || "").toLowerCase();
      if (key === "division") return (o.requested_division || o.division || "").toLowerCase();
      if (key === "parent") return (o.parent_name || o.primary_guardian_name || "").toLowerCase();
      if (key === "status") return (o.status || "").toLowerCase();
      if (key === "school") return (o.school || "").toLowerCase();
      if (key === "grade") return (o.grade || o.grade_2027 || "").toLowerCase();
      return o.created_at || "";
    };
    arr.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (typeof va === "number" && typeof vb === "number") return sign * (va - vb);
      return sign * String(va).localeCompare(String(vb));
    });
    return arr;
  }, [athletes, regSort, regFilterSport, regFilterStatus]);

  const filteredPre = React.useMemo(() => {
    let arr = [...preList];
    if (prePgm !== "all") arr = arr.filter(p => p.program === prePgm);
    const [key, dir] = preSort.split("_");
    const sign = dir === "asc" ? 1 : -1;
    const getVal = (o) => {
      if (key === "name") return (o.child_full_name || "").toLowerCase();
      if (key === "age") return Number(o.age ?? 0);
      if (key === "program" || key === "sport") return (o.program || "").toLowerCase();
      if (key === "school") return (o.school || "").toLowerCase();
      if (key === "grade") return (o.grade || "").toLowerCase();
      return o.created_at || "";
    };
    arr.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (typeof va === "number" && typeof vb === "number") return sign * (va - vb);
      return sign * String(va).localeCompare(String(vb));
    });
    return arr;
  }, [preList, preSort, prePgm]);

  // Age-Bucket Insights — group pre-registrations by division bracket × sport
  const ageBuckets = React.useMemo(() => {
    const rows = [
      { key: "6U",  label: "6U",  match: (age) => age >= 4 && age <= 6 },
      { key: "8U",  label: "8U",  match: (age) => age === 7 || age === 8 },
      { key: "10U", label: "10U", match: (age) => age === 9 || age === 10 },
      { key: "12U", label: "12U", match: (age) => age === 11 || age === 12 },
      { key: "13+", label: "13+", match: (age) => age >= 13 },
      { key: "?",   label: "Unspecified", match: (age) => !Number.isFinite(age) || age < 4 },
    ];
    return rows.map((r) => {
      const list = preList.filter((p) => r.match(Number(p.age)));
      const football = list.filter((p) => p.program === "football").length;
      const cheer = list.filter((p) => p.program === "cheer").length;
      const seven = list.filter((p) => p.program === "7v7").length;
      return { ...r, football, cheer, seven, total: football + cheer + seven, hidden: r.key === "?" && football + cheer + seven === 0 };
    });
  }, [preList]);

  const sortedSponsors = React.useMemo(() => {
    const arr = [...sponsors];
    if (spSort === "manual") {
      arr.sort((a, b) => (a.order || 0) - (b.order || 0));
    } else {
      const [key, dir] = spSort.split("_");
      arr.sort((a, b) => {
        let va = key === "name" ? (a.name || "") : key === "level" ? (a.level || "") : (a.created_at || "");
        let vb = key === "name" ? (b.name || "") : key === "level" ? (b.level || "") : (b.created_at || "");
        return (dir === "asc" ? 1 : -1) * String(va).localeCompare(String(vb));
      });
    }
    return arr;
  }, [sponsors, spSort]);

  const sortedContacts = React.useMemo(() => {
    const arr = [...contacts];
    const [key, dir] = contactSort.split("_");
    arr.sort((a, b) => {
      let va = key === "name" ? (a.name || "") : key === "subject" ? (a.subject || "") : (a.created_at || "");
      let vb = key === "name" ? (b.name || "") : key === "subject" ? (b.subject || "") : (b.created_at || "");
      return (dir === "asc" ? 1 : -1) * String(va).localeCompare(String(vb));
    });
    return arr;
  }, [contacts, contactSort]);

  const sortedInquiries = React.useMemo(() => {
    const arr = [...inquiries];
    const [key, dir] = inquirySort.split("_");
    arr.sort((a, b) => {
      let va = key === "business" ? (a.business_name || "") : key === "tier" ? (a.selected_tier || a.sponsorship_interest || "") : (a.created_at || "");
      let vb = key === "business" ? (b.business_name || "") : key === "tier" ? (b.selected_tier || b.sponsorship_interest || "") : (b.created_at || "");
      return (dir === "asc" ? 1 : -1) * String(va).localeCompare(String(vb));
    });
    return arr;
  }, [inquiries, inquirySort]);

  const persistSponsorOrder = async (list) => {
    try {
      await api.patch("/admin/sponsors/reorder", { ordered_ids: list.map(s => s.id) });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reorder failed"); }
  };

  const moveSponsor = (idx, dir) => {
    if (spSort !== "manual") { setSpSort("manual"); }
    const list = [...sortedSponsors];
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    setSponsors(list.map((s, i) => ({ ...s, order: i + 1 })));
    persistSponsorOrder(list);
  };

  const onSponsorDragStart = (idx) => (e) => {
    if (spSort !== "manual") setSpSort("manual");
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
  };
  const onSponsorDragOver = (idx) => (e) => { e.preventDefault(); setDragOverIdx(idx); };
  const onSponsorDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
    const list = [...sortedSponsors];
    const [moved] = list.splice(dragIdx, 1);
    list.splice(idx, 0, moved);
    setSponsors(list.map((s, i) => ({ ...s, order: i + 1 })));
    persistSponsorOrder(list);
    setDragIdx(null); setDragOverIdx(null);
  };

  useEffect(() => { load().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load")); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/athletes/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const postAnnouncement = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/announcements", ann);
      toast.success("Announcement posted");
      setAnn({ title: "", body: "", category: "General", pinned: false });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const postEvent = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/events", event);
      toast.success("Event created");
      setEvent({ ...event, title: "", date: "", time: "", location: "", opponent: "", notes: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const cards = [
    { label: "Total Athletes", value: stats?.total_athletes ?? '—', icon: Users, color: "text-scu-purple" },
    { label: "Football", value: stats?.football_registrations ?? '—', icon: Trophy, color: "text-scu-purple" },
    { label: "Cheer", value: stats?.cheer_registrations ?? '—', icon: Sparkles, color: "text-scu-purple" },
    { label: "Pending", value: stats?.pending_registrations ?? '—', icon: Clock, color: "text-amber-600" },
    { label: "Approved", value: stats?.approved_athletes ?? '—', icon: CheckCircle2, color: "text-emerald-600" },
    { label: "Payments Due", value: stats?.payments_due ?? '—', icon: DollarSign, color: "text-scu-purple" },
    { label: "Upcoming Events", value: stats?.upcoming_events ?? '—', icon: CalIcon, color: "text-scu-purple" },
    { label: "Unread Msgs", value: stats?.unread_messages ?? '—', icon: Mail, color: "text-amber-600" },
  ];

  return (
    <div data-testid="admin-dashboard" className="bg-scu-off min-h-[80vh]">
      <div className="border-b border-scu-smoke/15 bg-white">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-8">
          <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">Administrator</div>
          <h1 className="mt-1 font-bebas text-4xl md:text-5xl tracking-wide text-scu-obsidian">SCU Command Center</h1>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-10 space-y-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {cards.map((c) => (
            <div key={c.label} className="bg-white border border-scu-smoke/15 rounded-md p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-scu-smoke font-bold">
                <c.icon className={`h-4 w-4 ${c.color}`} /> {c.label}
              </div>
              <div className="mt-3 font-bebas text-4xl tracking-wide text-scu-obsidian">{c.value}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="registrations">
          <TabsList className="bg-white border border-scu-smoke/15 flex-wrap h-auto">
            <TabsTrigger data-testid="tab-registrations" value="registrations">Registrations</TabsTrigger>
            <TabsTrigger data-testid="tab-pre-reg" value="prereg">Pre-Registrations</TabsTrigger>
            <TabsTrigger data-testid="tab-announcements" value="announcements">Announcements</TabsTrigger>
            <TabsTrigger data-testid="tab-events" value="events">Events</TabsTrigger>
            <TabsTrigger data-testid="tab-sponsors" value="sponsors">Sponsors</TabsTrigger>
            <TabsTrigger data-testid="tab-teams" value="teams">Teams</TabsTrigger>
            <TabsTrigger data-testid="tab-messages" value="messages">
              <span>Messages</span>
              {((stats?.unread_messages || 0) + (stats?.unread_sponsor_inquiries || 0)) > 0 && (
                <span data-testid="tab-messages-badge" className="ml-2 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold leading-none">
                  {(stats?.unread_messages || 0) + (stats?.unread_sponsor_inquiries || 0)}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="registrations" className="mt-6 space-y-4">
            <div className="flex flex-wrap gap-2 items-center bg-white border border-scu-smoke/15 rounded-md p-3">
              <Select value={regSort} onValueChange={setRegSort}>
                <SelectTrigger data-testid="reg-sort" className="w-56"><SelectValue placeholder="Sort" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_desc">Newest first</SelectItem>
                  <SelectItem value="created_asc">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Athlete Name A→Z</SelectItem>
                  <SelectItem value="name_desc">Athlete Name Z→A</SelectItem>
                  <SelectItem value="age_asc">Age (Youngest first)</SelectItem>
                  <SelectItem value="age_desc">Age (Oldest first)</SelectItem>
                  <SelectItem value="sport_asc">Sport A→Z</SelectItem>
                  <SelectItem value="division_asc">Division A→Z</SelectItem>
                  <SelectItem value="parent_asc">Parent Name A→Z</SelectItem>
                  <SelectItem value="parent_desc">Parent Name Z→A</SelectItem>
                  <SelectItem value="status_asc">Status</SelectItem>
                  <SelectItem value="school_asc">School A→Z</SelectItem>
                  <SelectItem value="grade_asc">Grade Low→High</SelectItem>
                </SelectContent>
              </Select>
              <Select value={regFilterSport} onValueChange={setRegFilterSport}>
                <SelectTrigger data-testid="reg-filter-sport" className="w-40"><SelectValue placeholder="Sport" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sports</SelectItem>
                  <SelectItem value="football">Football</SelectItem>
                  <SelectItem value="cheer">Cheer</SelectItem>
                </SelectContent>
              </Select>
              <Select value={regFilterStatus} onValueChange={setRegFilterStatus}>
                <SelectTrigger data-testid="reg-filter-status" className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUS_ORDER.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-scu-smoke ml-auto">{filteredAthletes.length} of {athletes.length}</div>
              <Button
                data-testid="reg-migrate-all"
                size="sm"
                variant="outline"
                className="border-scu-purple/40 text-scu-purple hover:bg-scu-purple hover:text-white font-bold uppercase tracking-widest text-xs"
                onClick={async () => {
                  if (!window.confirm(`Move all ${athletes.length} registration record${athletes.length === 1 ? "" : "s"} into Pre-Registration? This is safe (dedup by name + program) and preserves parent info.`)) return;
                  try {
                    const res = await api.post("/admin/migrate-athletes-to-prereg");
                    toast.success(res.data?.ok ? "Registrations moved to Pre-Registration" : "Done");
                    load();
                  } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                }}
              >
                Move All → Pre-Reg
              </Button>
            </div>
            <div className="bg-white border border-scu-smoke/15 rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-scu-off text-scu-smoke text-xs uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-4 py-3">Athlete</th>
                    <th className="text-left px-4 py-3">Sport · Div</th>
                    <th className="text-left px-4 py-3">Parent</th>
                    <th className="text-left px-4 py-3">School · Grade</th>
                    <th className="text-left px-4 py-3">Submitted</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Team</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAthletes.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-scu-smoke">No registrations match filters.</td></tr>}
                  {filteredAthletes.map((a) => (
                    <React.Fragment key={a.id}>
                    <tr className="border-t border-scu-smoke/10 hover:bg-scu-off cursor-pointer" data-testid={`admin-athlete-row-${a.id}`}
                        onClick={() => setExpandedRow(expandedRow === a.id ? null : a.id)}>
                      <td className="px-4 py-3 font-bold text-scu-obsidian">{a.first_name} {a.last_name}<div className="text-xs text-scu-smoke">Age {a.age_as_of_cutoff || "—"}</div></td>
                      <td className="px-4 py-3 capitalize">{a.sport}<div className="text-xs text-scu-smoke">{a.requested_division}</div></td>
                      <td className="px-4 py-3">{a.parent_name}<br /><span className="text-xs text-scu-smoke">{a.parent_email}</span><br /><span className="text-xs text-scu-smoke">{a.parent_phone}</span></td>
                      <td className="px-4 py-3">{a.school}<div className="text-xs text-scu-smoke">Grade {a.grade}</div></td>
                      <td className="px-4 py-3 text-xs text-scu-smoke">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}</td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                          <SelectTrigger data-testid={`status-select-${a.id}`} className="w-36"><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUS_ORDER.map(s => <SelectItem key={s} value={s}>{s.replace('_',' ')}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <Select value={a.team_id || "none"} onValueChange={async (v) => { try { await api.patch(`/admin/athletes/${a.id}/team`, { team_id: v === "none" ? null : v }); toast.success("Team updated"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } }}>
                          <SelectTrigger data-testid={`team-select-${a.id}`} className="w-40"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Unassigned</SelectItem>
                            {teams.filter(t => t.sport === (a.sport || a.program)).map(t => (
                              <SelectItem key={t.id} value={t.id}>{t.name} · {t.division}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <Button data-testid={`reg-del-${a.id}`} onClick={() => deleteAthlete(a.id)} variant="ghost" size="sm" className="text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                    {expandedRow === a.id && (
                      <tr className="border-t border-scu-smoke/10 bg-scu-off"><td colSpan={8} className="px-6 py-4 text-sm">
                        <div className="grid md:grid-cols-3 gap-3 text-xs">
                          <div><b>Address:</b> {a.home_address}, {a.city_state_zip}</div>
                          <div><b>Emergency:</b> {a.emergency_1_name} · {a.emergency_1_phone}</div>
                          <div><b>Allergies:</b> {a.allergies}</div>
                          <div><b>Medications:</b> {a.medications}</div>
                          <div><b>Conditions:</b> {a.conditions}</div>
                          <div><b>Media:</b> {a.media_consent}</div>
                          <div><b>Uniform:</b> Jersey {a.jersey_size} · Pants {a.pants_size} · T {a.tshirt_size}</div>
                          <div><b>Signed:</b> {a.signature_typed} · {a.signed_at ? new Date(a.signed_at).toLocaleString() : "—"}</div>
                          <div><b>Payment:</b> {a.payment_status}</div>
                        </div>
                      </td></tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="announcements" className="mt-6">
            <Card className="border-scu-smoke/15 p-6">
              <div className="font-bebas text-2xl tracking-wide text-scu-obsidian">Post Announcement</div>
              <form onSubmit={postAnnouncement} className="mt-4 grid md:grid-cols-2 gap-4">
                <F label="Title"><Input data-testid="ann-title" required value={ann.title} onChange={(e) => setAnn({ ...ann, title: e.target.value })} /></F>
                <F label="Category"><Input data-testid="ann-cat" value={ann.category} onChange={(e) => setAnn({ ...ann, category: e.target.value })} /></F>
                <div className="md:col-span-2"><F label="Body"><Textarea data-testid="ann-body" required rows={4} value={ann.body} onChange={(e) => setAnn({ ...ann, body: e.target.value })} /></F></div>
                <div className="md:col-span-2">
                  <Button data-testid="ann-submit" type="submit" className="bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest">Publish</Button>
                </div>
              </form>
            </Card>
          </TabsContent>

          <TabsContent value="events" className="mt-6">
            <Card className="border-scu-smoke/15 p-6">
              <div className="font-bebas text-2xl tracking-wide text-scu-obsidian">Create Event</div>
              <form onSubmit={postEvent} className="mt-4 grid md:grid-cols-2 gap-4">
                <F label="Title"><Input data-testid="ev-title" required value={event.title} onChange={(e) => setEvent({ ...event, title: e.target.value })} /></F>
                <F label="Date"><Input data-testid="ev-date" required type="date" value={event.date} onChange={(e) => setEvent({ ...event, date: e.target.value })} /></F>
                <F label="Time"><Input data-testid="ev-time" required value={event.time} onChange={(e) => setEvent({ ...event, time: e.target.value })} /></F>
                <F label="Location"><Input data-testid="ev-loc" required value={event.location} onChange={(e) => setEvent({ ...event, location: e.target.value })} /></F>
                <F label="Sport">
                  <Select value={event.sport} onValueChange={(v) => setEvent({ ...event, sport: v })}>
                    <SelectTrigger data-testid="ev-sport"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="football">Football</SelectItem><SelectItem value="cheer">Cheer</SelectItem><SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <F label="Type">
                  <Select value={event.event_type} onValueChange={(v) => setEvent({ ...event, event_type: v })}>
                    <SelectTrigger data-testid="ev-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="practice">Practice</SelectItem><SelectItem value="game">Game</SelectItem><SelectItem value="event">Event</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <F label="Division"><Input data-testid="ev-div" value={event.division} onChange={(e) => setEvent({ ...event, division: e.target.value })} /></F>
                <F label="Opponent"><Input data-testid="ev-opp" value={event.opponent} onChange={(e) => setEvent({ ...event, opponent: e.target.value })} /></F>
                <div className="md:col-span-2"><F label="Notes"><Textarea data-testid="ev-notes" rows={2} value={event.notes} onChange={(e) => setEvent({ ...event, notes: e.target.value })} /></F></div>
                <div className="md:col-span-2">
                  <Button data-testid="ev-submit" type="submit" className="bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest">Create Event</Button>
                </div>
              </form>
            </Card>
          </TabsContent>

          <TabsContent value="prereg" className="mt-6 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Total Pre-Reg", value: preStats?.total ?? "—", key: "all" },
                { label: "Football", value: preStats?.football ?? "—", key: "football" },
                { label: "Cheer", value: preStats?.cheer ?? "—", key: "cheer" },
                { label: "7v7", value: preStats?.seven_v_seven ?? "—", key: "7v7" },
              ].map((c) => (
                <button key={c.label} onClick={() => setPrePgm(c.key)} data-testid={`prereg-card-${c.key}`}
                        className={`text-left bg-white border rounded-md p-5 hover:border-scu-purple transition-colors ${prePgm === c.key ? "border-scu-purple" : "border-scu-smoke/15"}`}>
                  <div className="text-xs uppercase tracking-widest text-scu-smoke font-bold">{c.label}</div>
                  <div className="mt-2 font-bebas text-4xl text-scu-purple">{c.value}</div>
                </button>
              ))}
            </div>

            {/* Age-Bucket Insights */}
            <div className="bg-white border border-scu-smoke/15 rounded-md p-5" data-testid="age-buckets">
              <div className="flex items-center gap-2 mb-4">
                <UsersRound className="h-4 w-4 text-scu-purple" />
                <div className="text-xs uppercase tracking-[0.3em] text-scu-smoke font-bold">Age-Bucket Insights</div>
                <div className="text-[10px] uppercase tracking-widest text-scu-smoke/70 ml-2">Division fill by age bracket</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-scu-smoke text-[10px] uppercase tracking-widest">
                    <tr>
                      <th className="text-left px-3 py-2">Division</th>
                      <th className="text-right px-3 py-2">Football</th>
                      <th className="text-right px-3 py-2">Cheer</th>
                      <th className="text-right px-3 py-2">7v7</th>
                      <th className="text-right px-3 py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ageBuckets.filter(b => !b.hidden).map((b) => (
                      <tr key={b.key} className="border-t border-scu-smoke/10" data-testid={`bucket-${b.key.toLowerCase().replace('+','plus').replace('?','na')}`}>
                        <td className="px-3 py-3">
                          <span className="font-bebas text-2xl tracking-wide text-scu-purple">{b.label}</span>
                          <span className="ml-2 text-[10px] uppercase tracking-widest text-scu-smoke/70">
                            {b.key === "6U" && "Ages 5-6"}
                            {b.key === "8U" && "Ages 7-8"}
                            {b.key === "10U" && "Ages 9-10"}
                            {b.key === "12U" && "Ages 11-12"}
                            {b.key === "13+" && "13 & older"}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right font-bebas text-2xl tracking-wide text-scu-obsidian">{b.football}</td>
                        <td className="px-3 py-3 text-right font-bebas text-2xl tracking-wide text-scu-obsidian">{b.cheer}</td>
                        <td className="px-3 py-3 text-right font-bebas text-2xl tracking-wide text-scu-obsidian">{b.seven}</td>
                        <td className="px-3 py-3 text-right font-bebas text-2xl tracking-wide text-scu-purple">{b.total}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-scu-purple/30 bg-scu-off">
                      <td className="px-3 py-3 text-[10px] uppercase tracking-widest text-scu-smoke font-bold">All Ages</td>
                      <td className="px-3 py-3 text-right font-bebas text-2xl tracking-wide text-scu-purple">{ageBuckets.reduce((s, b) => s + b.football, 0)}</td>
                      <td className="px-3 py-3 text-right font-bebas text-2xl tracking-wide text-scu-purple">{ageBuckets.reduce((s, b) => s + b.cheer, 0)}</td>
                      <td className="px-3 py-3 text-right font-bebas text-2xl tracking-wide text-scu-purple">{ageBuckets.reduce((s, b) => s + b.seven, 0)}</td>
                      <td className="px-3 py-3 text-right font-bebas text-2xl tracking-wide text-scu-purple">{ageBuckets.reduce((s, b) => s + b.total, 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 items-center bg-white border border-scu-smoke/15 rounded-md p-3">
              <Select value={preSort} onValueChange={setPreSort}>
                <SelectTrigger data-testid="pre-sort" className="w-56"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_desc">Newest first</SelectItem>
                  <SelectItem value="created_asc">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Athlete Name A→Z</SelectItem>
                  <SelectItem value="name_desc">Athlete Name Z→A</SelectItem>
                  <SelectItem value="age_asc">Age (Youngest first)</SelectItem>
                  <SelectItem value="age_desc">Age (Oldest first)</SelectItem>
                  <SelectItem value="program_asc">Sport / Program A→Z</SelectItem>
                  <SelectItem value="school_asc">School A→Z</SelectItem>
                  <SelectItem value="grade_asc">Grade Low→High</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-scu-smoke ml-auto">{filteredPre.length} of {preList.length}</div>
            </div>
            <div className="bg-white border border-scu-smoke/15 rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-scu-off text-scu-smoke text-xs uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Age</th>
                    <th className="text-left px-4 py-3">Grade</th>
                    <th className="text-left px-4 py-3">School</th>
                    <th className="text-left px-4 py-3">Program</th>
                    <th className="text-left px-4 py-3">Parent Email</th>
                    <th className="text-left px-4 py-3">Submitted</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPre.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-scu-smoke">No pre-registrations match filters.</td></tr>}
                  {filteredPre.map((p) => (
                    <tr key={p.id} className="border-t border-scu-smoke/10" data-testid={`prereg-row-${p.id}`}>
                      <td className="px-4 py-3 font-bold text-scu-obsidian">{p.child_full_name}</td>
                      <td className="px-4 py-3">{p.age}</td>
                      <td className="px-4 py-3">{p.grade}</td>
                      <td className="px-4 py-3">{p.school}</td>
                      <td className="px-4 py-3 capitalize">{p.program}</td>
                      <td className="px-4 py-3 text-scu-smoke">
                        {p.parent_guardian_email ? (
                          <a href={`mailto:${p.parent_guardian_email}`} className="text-scu-purple hover:underline" data-testid={`prereg-email-${p.id}`}>{p.parent_guardian_email}</a>
                        ) : <span className="text-scu-smoke/50">—</span>}
                        {p.parent_phone && <div className="text-xs text-scu-smoke/70 mt-0.5">{p.parent_phone}</div>}
                      </td>
                      <td className="px-4 py-3 text-scu-smoke">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-right">
                        <Button data-testid={`pre-del-${p.id}`} onClick={() => deletePre(p.id)} variant="ghost" size="sm" className="text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="sponsors" className="mt-6 space-y-6">
            <Card className="border-scu-smoke/15 p-6">
              <div className="font-bebas text-2xl tracking-wide text-scu-obsidian">Add Sponsor</div>
              <form onSubmit={async (e) => { e.preventDefault(); try { await api.post("/admin/sponsors", sponsor); toast.success("Sponsor added"); setSponsor({ name: "", level: "Community Sponsor", website: "", logo_url: "", story: "" }); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } }}
                    className="mt-4 grid md:grid-cols-2 gap-4">
                <F label="Business Name"><Input data-testid="sp-add-name" required value={sponsor.name} onChange={(e) => setSponsor({ ...sponsor, name: e.target.value })} /></F>
                <F label="Level">
                  <Select value={sponsor.level} onValueChange={(v) => setSponsor({ ...sponsor, level: v })}>
                    <SelectTrigger data-testid="sp-add-level"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Community Sponsor","Utes Supporter","United Partner","Founding Partner"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Website (optional)"><Input data-testid="sp-add-web" value={sponsor.website} onChange={(e) => setSponsor({ ...sponsor, website: e.target.value })} /></F>
                <div className="md:col-span-2">
                  <F label="Partner Story (optional — recommended for Founding Partners)">
                    <Textarea data-testid="sp-add-story" rows={3} value={sponsor.story} onChange={(e) => setSponsor({ ...sponsor, story: e.target.value })} placeholder="Short brand story shown on the Sponsors page Founding Partners section — e.g., 'Throne Barber Shop, LLC proudly stands behind the Utes as a Founding Partner…'" />
                  </F>
                </div>
                <div className="md:col-span-2">
                  <F label="Partner Quote (optional — appears under the sponsor's logo)">
                    <Input data-testid="sp-add-quote" value={sponsor.quote} onChange={(e) => setSponsor({ ...sponsor, quote: e.target.value })} placeholder='e.g., "Success is a relentless will to reach the best version of yourself!"' />
                  </F>
                </div>
                <F label="Logo (drag & drop or paste URL)">
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleLogoDrop}
                    className={`border-2 border-dashed rounded-md p-4 text-center transition-colors ${dragOver ? "border-scu-purple bg-scu-purple/5" : "border-scu-smoke/30 bg-scu-off"}`}
                    data-testid="sp-logo-drop"
                  >
                    {sponsor.logo_url ? (
                      <div className="flex flex-col items-center gap-2">
                        <img src={sponsor.logo_url} alt="Logo preview" className="max-h-24 max-w-[80%] object-contain" />
                        <div className="flex gap-2">
                          <label className="text-xs uppercase tracking-widest font-bold text-scu-purple hover:text-scu-electric cursor-pointer">
                            Replace
                            <input data-testid="sp-logo-input" type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && readLogoFile(e.target.files[0])} />
                          </label>
                          <button data-testid="sp-logo-remove" type="button" onClick={() => setSponsor({ ...sponsor, logo_url: "" })} className="text-xs uppercase tracking-widest font-bold text-red-600 hover:text-red-800">Remove</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2 py-3">
                        <Upload className="h-6 w-6 text-scu-smoke" />
                        <div className="text-sm text-scu-smoke">Drag & drop a logo here, or</div>
                        <label className="text-xs uppercase tracking-widest font-bold text-scu-purple hover:text-scu-electric cursor-pointer">
                          Browse files
                          <input data-testid="sp-logo-input" type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && readLogoFile(e.target.files[0])} />
                        </label>
                        <div className="text-[10px] uppercase tracking-widest text-scu-smoke/70">or paste URL below</div>
                        <Input data-testid="sp-add-logo" placeholder="https://…" value={sponsor.logo_url} onChange={(e) => setSponsor({ ...sponsor, logo_url: e.target.value })} />
                      </div>
                    )}
                  </div>
                </F>
                <div className="md:col-span-2"><Button data-testid="sp-add-submit" type="submit" className="bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest">Add Sponsor</Button></div>
              </form>
            </Card>
            <div className="flex flex-wrap gap-2 items-center bg-white border border-scu-smoke/15 rounded-md p-3">
              <Select value={spSort} onValueChange={setSpSort}>
                <SelectTrigger data-testid="sp-sort" className="w-56"><SelectValue placeholder="Sort" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual order (drag / arrows)</SelectItem>
                  <SelectItem value="name_asc">Name A→Z</SelectItem>
                  <SelectItem value="name_desc">Name Z→A</SelectItem>
                  <SelectItem value="level_asc">Level A→Z</SelectItem>
                  <SelectItem value="created_desc">Newest first</SelectItem>
                  <SelectItem value="created_asc">Oldest first</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-scu-smoke ml-auto hidden md:block">Drag cards to reorder · Use ▲ ▼ on mobile</div>
              <div className="text-xs text-scu-smoke ml-auto md:hidden">Use ▲ ▼ to reorder</div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {sortedSponsors.map((s, idx) => (
                <div key={s.id}
                     draggable={spSort === "manual"}
                     onDragStart={onSponsorDragStart(idx)}
                     onDragOver={onSponsorDragOver(idx)}
                     onDrop={onSponsorDrop(idx)}
                     onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                     className={`bg-white border rounded-md p-4 flex flex-col gap-2 transition-all ${spSort === "manual" ? "cursor-move" : ""} ${dragOverIdx === idx && dragIdx !== null && dragIdx !== idx ? "border-scu-purple ring-2 ring-scu-purple/30" : "border-scu-smoke/15"}`}
                     data-testid={`admin-sponsor-${s.id}`}>
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-scu-smoke">
                    <div className="flex items-center gap-1">
                      <GripVertical className={`h-3.5 w-3.5 ${spSort === "manual" ? "text-scu-purple" : "text-scu-smoke/40"}`} />
                      <span>#{idx + 1}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button data-testid={`sp-up-${s.id}`} size="icon" variant="ghost" disabled={idx === 0} onClick={() => moveSponsor(idx, -1)} className="h-7 w-7 text-scu-purple hover:bg-scu-purple/10 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></Button>
                      <Button data-testid={`sp-down-${s.id}`} size="icon" variant="ghost" disabled={idx === sortedSponsors.length - 1} onClick={() => moveSponsor(idx, 1)} className="h-7 w-7 text-scu-purple hover:bg-scu-purple/10 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  <div className="h-24 bg-scu-off rounded flex items-center justify-center overflow-hidden">
                    {s.logo_url ? <img src={s.logo_url} alt={s.name} className="max-h-20 max-w-[85%] object-contain" /> : <span className="text-scu-smoke text-xs">No logo</span>}
                  </div>
                  <div className="text-sm font-bold text-scu-obsidian">{s.name}</div>
                  <div className="flex items-center gap-2">
                    <Select
                      value={s.level || "Community Sponsor"}
                      onValueChange={async (v) => {
                        try {
                          await api.patch(`/admin/sponsors/${s.id}`, { name: s.name, level: v, website: s.website, logo_url: s.logo_url, story: s.story, quote: s.quote });
                          toast.success(v === "Founding Partner" ? "Promoted to Founding Partner" : "Level updated");
                          load();
                        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                      }}
                    >
                      <SelectTrigger data-testid={`sp-level-${s.id}`} className="h-7 text-[10px] uppercase tracking-widest font-bold border-scu-smoke/20"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Community Sponsor","Utes Supporter","United Partner","Founding Partner"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    data-testid={`sp-story-${s.id}`}
                    variant="ghost"
                    className="h-7 text-[10px] uppercase tracking-widest font-bold text-scu-purple hover:bg-scu-purple/10 justify-start px-2"
                    onClick={async () => {
                      const next = window.prompt(`Edit partner story for ${s.name}`, s.story || "");
                      if (next === null) return;
                      try {
                        await api.patch(`/admin/sponsors/${s.id}`, { name: s.name, level: s.level, website: s.website, logo_url: s.logo_url, story: next, quote: s.quote });
                        toast.success("Story saved");
                        load();
                      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                    }}
                  >
                    {s.story ? "Edit story" : "Add story"}
                  </Button>
                  <Button
                    data-testid={`sp-quote-${s.id}`}
                    variant="ghost"
                    className="h-7 text-[10px] uppercase tracking-widest font-bold text-scu-purple hover:bg-scu-purple/10 justify-start px-2"
                    onClick={async () => {
                      const next = window.prompt(`Edit partner quote for ${s.name} (appears under their logo)`, s.quote || "");
                      if (next === null) return;
                      try {
                        await api.patch(`/admin/sponsors/${s.id}`, { name: s.name, level: s.level, website: s.website, logo_url: s.logo_url, story: s.story, quote: next });
                        toast.success("Quote saved");
                        load();
                      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                    }}
                  >
                    {s.quote ? "Edit quote" : "Add quote"}
                  </Button>
                  <Button data-testid={`sp-del-${s.id}`} onClick={async () => { if (!window.confirm(`Delete ${s.name}?`)) return; try { await api.delete(`/admin/sponsors/${s.id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } }}
                          variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-8 text-xs">Remove</Button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="teams" className="mt-6 space-y-6">
            <Card className="border-scu-smoke/15 p-6">
              <div className="font-bebas text-2xl tracking-wide text-scu-obsidian">Create Team</div>
              <form onSubmit={async (e) => { e.preventDefault(); try { await api.post("/admin/teams", teamForm); toast.success("Team created"); setTeamForm({ name: "", sport: "football", division: "", coach_name: "", notes: "" }); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } }}
                    className="mt-4 grid md:grid-cols-2 gap-4">
                <F label="Team Name"><Input data-testid="team-name" required value={teamForm.name} onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })} placeholder="e.g. Utes 8U Purple" /></F>
                <F label="Sport">
                  <Select value={teamForm.sport} onValueChange={(v) => setTeamForm({ ...teamForm, sport: v })}>
                    <SelectTrigger data-testid="team-sport"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="football">Football</SelectItem>
                      <SelectItem value="cheer">Cheer</SelectItem>
                      <SelectItem value="7v7">7v7</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <F label="Division"><Input data-testid="team-division" required value={teamForm.division} onChange={(e) => setTeamForm({ ...teamForm, division: e.target.value })} placeholder="e.g. 8U" /></F>
                <F label="Head Coach (optional)"><Input data-testid="team-coach" value={teamForm.coach_name} onChange={(e) => setTeamForm({ ...teamForm, coach_name: e.target.value })} /></F>
                <div className="md:col-span-2"><F label="Notes (optional)"><Input data-testid="team-notes" value={teamForm.notes} onChange={(e) => setTeamForm({ ...teamForm, notes: e.target.value })} /></F></div>
                <div className="md:col-span-2"><Button data-testid="team-submit" type="submit" className="bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest">Create Team</Button></div>
              </form>
            </Card>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {teams.length === 0 && <div className="col-span-full text-scu-smoke text-sm bg-white border border-scu-smoke/15 rounded-md p-8 text-center">No teams yet. Create your first team above, then assign athletes from the Registrations tab.</div>}
              {teams.map((t) => (
                <Card key={t.id} className="border-scu-smoke/15 p-5" data-testid={`team-card-${t.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-scu-purple font-bold">
                        <Shield className="h-3.5 w-3.5" /> {t.sport} · {t.division}
                      </div>
                      <div className="mt-1 font-bebas text-2xl tracking-wide text-scu-obsidian leading-tight">{t.name}</div>
                      {t.coach_name && <div className="text-xs text-scu-smoke mt-1">Coach: {t.coach_name}</div>}
                    </div>
                    <Button data-testid={`team-del-${t.id}`} size="icon" variant="ghost" onClick={async () => { if (!window.confirm(`Delete ${t.name}? Assigned athletes will be unassigned.`)) return; try { await api.delete(`/admin/teams/${t.id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } }} className="text-red-600 hover:bg-red-50 h-8 w-8"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="mt-4 pt-3 border-t border-scu-smoke/10">
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-scu-smoke font-bold mb-2">
                      <span>Roster</span>
                      <span className="text-scu-purple">{t.roster_count} athlete{t.roster_count === 1 ? "" : "s"}</span>
                    </div>
                    {t.roster_count === 0 && <div className="text-xs text-scu-smoke italic">No athletes assigned yet. Use the Team dropdown on the Registrations tab.</div>}
                    <ul className="space-y-1 text-sm">
                      {(t.roster || []).map((r) => (
                        <li key={r.id} className="flex items-center justify-between">
                          <span className="text-scu-obsidian">{r.first_name} {r.last_name}</span>
                          <span className="text-xs text-scu-smoke">Age {r.age_as_of_cutoff}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {t.notes && <div className="mt-3 pt-3 border-t border-scu-smoke/10 text-xs text-scu-smoke">{t.notes}</div>}
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="messages" className="mt-6 space-y-8">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="text-xs uppercase tracking-[0.3em] text-scu-smoke font-bold">Contact Inquiries</div>
                <Select value={contactSort} onValueChange={setContactSort}>
                  <SelectTrigger data-testid="contact-sort" className="w-52 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_desc">Newest first</SelectItem>
                    <SelectItem value="created_asc">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name A→Z</SelectItem>
                    <SelectItem value="name_desc">Name Z→A</SelectItem>
                    <SelectItem value="subject_asc">Subject A→Z</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-xs text-scu-smoke ml-auto">{sortedContacts.length} total</div>
              </div>
              <div className="mt-3 space-y-3">
                {sortedContacts.length === 0 && <div className="text-scu-smoke text-sm">No contact messages yet.</div>}
                {sortedContacts.map((c) => (
                  <Card key={c.id} className={`border-scu-smoke/15 p-5 ${!c.read ? "ring-2 ring-red-500/40 bg-red-50/30" : ""}`} data-testid={`contact-${c.id}`}
                        onClick={() => { if (!c.read) { markContactRead(c.id); setContacts((prev) => prev.map((x) => x.id === c.id ? { ...x, read: true } : x)); setStats((prev) => prev ? { ...prev, unread_messages: Math.max(0, (prev.unread_messages || 0) - 1) } : prev); } }}>
                    {!c.read && <span data-testid={`contact-unread-${c.id}`} className="inline-block mb-2 bg-red-600 text-white text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded">● New</span>}
                    <div className="flex justify-between text-sm text-scu-smoke"><span className="font-bold text-scu-obsidian">{c.name}{c.email ? <span className="font-normal text-scu-smoke"> · <a href={`mailto:${c.email}`} className="text-scu-purple hover:underline">{c.email}</a></span> : null}</span><span>{new Date(c.created_at).toLocaleDateString()}</span></div>
                    <div className="font-bold text-scu-obsidian mt-1 text-base">{c.subject}</div>
                    <div className="text-sm text-scu-smoke mt-1 whitespace-pre-wrap">{c.message}</div>
                    <div className="mt-3 flex items-center gap-2">
                      <Button data-testid={`contact-reply-${c.id}`} size="sm" onClick={() => openReply("contact", c)} className="bg-scu-purple hover:bg-scu-electric text-white text-xs font-bold uppercase tracking-widest">
                        <Reply className="h-3.5 w-3.5 mr-1" /> Reply
                      </Button>
                      <Button data-testid={`contact-del-${c.id}`} size="sm" variant="outline" onClick={async () => { if (!window.confirm("Delete this message? This cannot be undone.")) return; try { await api.delete(`/admin/contact/${c.id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } }} className="text-red-600 border-red-200 hover:bg-red-50 text-xs font-bold uppercase tracking-widest">
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                      </Button>
                      {c.replied && <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded">Replied · {c.replied_at ? new Date(c.replied_at).toLocaleDateString() : ""}</span>}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="text-xs uppercase tracking-[0.3em] text-scu-smoke font-bold">Sponsorship Inquiries</div>
                <Select value={inquirySort} onValueChange={setInquirySort}>
                  <SelectTrigger data-testid="inquiry-sort" className="w-52 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_desc">Newest first</SelectItem>
                    <SelectItem value="created_asc">Oldest first</SelectItem>
                    <SelectItem value="business_asc">Business A→Z</SelectItem>
                    <SelectItem value="business_desc">Business Z→A</SelectItem>
                    <SelectItem value="tier_asc">Tier</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-xs text-scu-smoke ml-auto">{sortedInquiries.length} total</div>
              </div>
              <div className="mt-3 space-y-3">
                {sortedInquiries.length === 0 && <div className="text-scu-smoke text-sm">No sponsor inquiries yet.</div>}
                {sortedInquiries.map((c) => (
                  <Card key={c.id} className={`border-scu-smoke/15 p-5 md:p-6 ${!c.read ? "ring-2 ring-red-500/40 bg-red-50/30" : ""}`} data-testid={`sponsor-inq-${c.id}`}
                        onClick={() => { if (!c.read) { markSponsorRead(c.id); setInquiries((prev) => prev.map((x) => x.id === c.id ? { ...x, read: true } : x)); setStats((prev) => prev ? { ...prev, unread_sponsor_inquiries: Math.max(0, (prev.unread_sponsor_inquiries || 0) - 1) } : prev); } }}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        {!c.read && <span data-testid={`unread-${c.id}`} className="inline-block mb-2 bg-red-600 text-white text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded">● New</span>}
                        <div className="text-[10px] uppercase tracking-[0.3em] text-scu-smoke font-bold">Inquiry From</div>
                        <div data-testid={`sponsor-inq-business-${c.id}`} className="font-bebas text-2xl md:text-3xl tracking-wide text-scu-obsidian leading-tight break-words">
                          {(c.contact_person || "—")}{c.business_name ? <span className="text-scu-purple"> — {c.business_name}</span> : null}
                        </div>
                      </div>
                      <div className="text-xs md:text-sm text-scu-smoke sm:text-right shrink-0">
                        {new Date(c.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm md:text-base">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-scu-smoke font-bold">Contact Person</div>
                        <div className="text-scu-obsidian">{c.contact_person || "—"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-scu-smoke font-bold">Email</div>
                        {c.email ? (
                          <a href={`mailto:${c.email}`} className="text-scu-purple hover:underline break-all">{c.email}</a>
                        ) : <div className="text-scu-smoke/60">—</div>}
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-scu-smoke font-bold">Phone</div>
                        {c.phone ? (
                          <a href={`tel:${c.phone}`} className="text-scu-purple hover:underline">{c.phone}</a>
                        ) : <div className="text-scu-smoke/60">—</div>}
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-scu-smoke font-bold">Sponsorship Interest</div>
                        <div className="text-scu-obsidian font-bold">
                          <span className="text-scu-purple">{c.selected_tier || c.sponsorship_interest || "—"}</span>
                          {c.contribution_amount ? <span className="text-scu-smoke font-normal"> · ${c.contribution_amount}</span> : null}
                        </div>
                      </div>
                      {c.website_or_social && (
                        <div className="sm:col-span-2">
                          <div className="text-[10px] uppercase tracking-widest text-scu-smoke font-bold">Website / Social</div>
                          <div className="text-scu-obsidian break-all">{c.website_or_social}</div>
                        </div>
                      )}
                      {(c.notes || c.message) && (
                        <div className="sm:col-span-2">
                          <div className="text-[10px] uppercase tracking-widest text-scu-smoke font-bold">Notes</div>
                          <div className="text-scu-obsidian whitespace-pre-wrap">{c.notes || c.message}</div>
                        </div>
                      )}
                    </div>
                    <div className="mt-5 flex flex-wrap items-center gap-2">
                      <Button data-testid={`sponsor-reply-${c.id}`} size="sm" onClick={() => openReply("sponsor_inquiry", c)} className="bg-scu-purple hover:bg-scu-electric text-white text-xs font-bold uppercase tracking-widest">
                        <Reply className="h-3.5 w-3.5 mr-1" /> Reply
                      </Button>
                      <Button data-testid={`sponsor-del-${c.id}`} size="sm" variant="outline" onClick={async () => { if (!window.confirm("Delete this sponsor inquiry? This cannot be undone.")) return; try { await api.delete(`/admin/sponsor-inquiries/${c.id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } }} className="text-red-600 border-red-200 hover:bg-red-50 text-xs font-bold uppercase tracking-widest">
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                      </Button>
                      {c.replied && <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded">Replied · {c.replied_at ? new Date(c.replied_at).toLocaleDateString() : ""}</span>}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <Dialog open={!!replyModal} onOpenChange={(o) => !o && setReplyModal(null)}>
          <DialogContent data-testid="reply-modal" className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-bebas text-2xl tracking-wide">Reply</DialogTitle>
            </DialogHeader>
            {replyModal && (
              <div className="space-y-4">
                <div className="text-sm">
                  <div className="text-xs uppercase tracking-widest text-scu-smoke font-bold">To</div>
                  <div className="font-bold text-scu-obsidian" data-testid="reply-to">{replyModal.inquiry.email}</div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-scu-smoke font-bold">Subject</Label>
                  <Input data-testid="reply-subject" value={replySubj} onChange={(e) => setReplySubj(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-scu-smoke font-bold">Message</Label>
                  <Textarea data-testid="reply-body" rows={6} value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder="Type your reply…" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-widest text-scu-smoke font-bold">Attachments</Label>
                    <span className="text-[10px] text-scu-smoke/70">Images · PDF · Word · 10 MB each (25 MB total)</span>
                  </div>
                  <div
                    data-testid="reply-dropzone"
                    onDragOver={(e) => { e.preventDefault(); setReplyDragOver(true); }}
                    onDragLeave={() => setReplyDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setReplyDragOver(false); addReplyFiles(e.dataTransfer.files); }}
                    className={`mt-1 border-2 border-dashed rounded-md p-4 text-center transition-colors ${replyDragOver ? "border-scu-purple bg-scu-purple/5" : "border-scu-smoke/30 bg-scu-off"}`}
                  >
                    <Upload className="h-5 w-5 text-scu-smoke mx-auto" />
                    <div className="mt-1 text-xs text-scu-smoke">Drag & drop files here, or</div>
                    <label className="mt-2 inline-flex items-center text-xs uppercase tracking-widest font-bold text-scu-purple hover:text-scu-electric cursor-pointer">
                      Attach Files
                      <input
                        data-testid="reply-file-input"
                        type="file"
                        multiple
                        accept="image/*,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        className="hidden"
                        onChange={(e) => { addReplyFiles(e.target.files); e.target.value = ""; }}
                      />
                    </label>
                  </div>
                  {replyFiles.length > 0 && (
                    <ul data-testid="reply-attachments" className="mt-2 space-y-1.5">
                      {replyFiles.map((f, idx) => (
                        <li key={idx} data-testid={`reply-attachment-${idx}`} className="flex items-center gap-2 bg-white border border-scu-smoke/15 rounded-md px-2.5 py-1.5">
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-bold text-scu-obsidian truncate">{f.name}</div>
                            <div className="text-[10px] text-scu-smoke">{(f.size / 1024).toFixed(1)} KB · {f.mime || "—"}</div>
                            {replySending && (
                              <div className="mt-1 h-1 w-full bg-scu-smoke/10 rounded overflow-hidden">
                                <div className="h-full bg-scu-purple transition-all" style={{ width: `${f.progress}%` }} />
                              </div>
                            )}
                          </div>
                          {!replySending && (
                            <Button data-testid={`reply-attachment-remove-${idx}`} size="icon" variant="ghost" onClick={() => removeReplyFile(idx)} className="h-7 w-7 text-red-600 hover:bg-red-50">
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button data-testid="reply-cancel" variant="outline" onClick={() => setReplyModal(null)} className="border-scu-smoke/30">Cancel</Button>
              <Button data-testid="reply-send" onClick={sendReply} disabled={replySending || !replyBody.trim()} className="bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest">
                {replySending ? "Sending…" : "Send"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-scu-smoke font-bold">{label}</Label>
      {children}
    </div>
  );
}
